import { Box, Button, Card, HStack, Portal, Tag, Text, Tooltip, VStack } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'

type Token = '{PREFIX}' | '{YYYY}' | '{YY}' | '{MM}' | '{RUNNING}'
type Item = { kind: 'token'; value: Token } | { kind: 'sep'; value: string } // user-defined separators (like "-" or "/")

const TOKENS: { label: string; token: Token; required?: boolean }[] = [
  { label: 'Präfix', token: '{PREFIX}' },
  { label: 'JJJJ', token: '{YYYY}' },
  { label: 'JJ', token: '{YY}' },
  { label: 'MM', token: '{MM}' },
  { label: 'Zähler', token: '{RUNNING}', required: true },
]

const DEFAULT_BUTTONS: Array<{ label: string; add: Item }> = [
  { label: 'Präfix', add: { kind: 'token', value: '{PREFIX}' } },
  { label: 'JJJJ', add: { kind: 'token', value: '{YYYY}' } },
  { label: 'JJ', add: { kind: 'token', value: '{YY}' } },
  { label: 'MM', add: { kind: 'token', value: '{MM}' } },
  { label: '-', add: { kind: 'sep', value: '-' } },
  { label: '/', add: { kind: 'sep', value: '/' } },
  { label: 'Zähler *', add: { kind: 'token', value: '{RUNNING}' } },
]
const TOKEN_TOOLTIPS: Record<Token, string> = {
  '{PREFIX}': 'Fester Präfix für alle Rechnungsnummern (z. B. RE, INV)',
  '{YYYY}': 'Vierstellige Jahreszahl (z. B. 2026)',
  '{YY}': 'Zweistellige Jahreszahl (z. B. 26)',
  '{MM}': 'Monat der Rechnung (01–12)',
  '{RUNNING}': 'Fortlaufende Nummer der Rechnung (Pflichtfeld)',
}

function stringify(items: Item[]) {
  return items.map(i => i.value).join('')
}

function parseTemplate(tpl: string): Item[] {
  if (!tpl?.trim()) return []
  const tokens = new Set(TOKENS.map(t => t.token))
  const out: Item[] = []

  let i = 0
  while (i < tpl.length) {
    const start = tpl.indexOf('{', i)
    if (start === -1) {
      // remaining is separators/text
      const rest = tpl.slice(i)
      if (rest) out.push({ kind: 'sep', value: rest })
      break
    }

    // text before token -> separator chunk
    if (start > i) {
      out.push({ kind: 'sep', value: tpl.slice(i, start) })
    }

    const end = tpl.indexOf('}', start)
    if (end === -1) {
      const rest = tpl.slice(start)
      out.push({ kind: 'sep', value: rest })
      break
    }

    const candidate = tpl.slice(start, end + 1) as Token | string
    if (tokens.has(candidate as Token)) {
      out.push({ kind: 'token', value: candidate as Token })
    } else {
      // unknown token -> treat as separator text so user can delete
      out.push({ kind: 'sep', value: candidate })
    }

    i = end + 1
  }

  // merge adjacent separators
  const merged: Item[] = []
  for (const it of out) {
    const prev = merged[merged.length - 1]
    if (it.kind === 'sep' && prev?.kind === 'sep') prev.value += it.value
    else merged.push({ ...it })
  }

  return merged.filter(i => i.value !== '')
}
export function renderPreview(tpl: string, ctx: { prefix: string; running: string }) {
  const now = new Date()
  const yyyy = String(now.getFullYear())
  const yy = yyyy.slice(-2)
  const mm = String(now.getMonth() + 1).padStart(2, '0')

  return (tpl || '')
    .replaceAll('{PREFIX}', ctx.prefix ?? '')
    .replaceAll('{YYYY}', yyyy)
    .replaceAll('{YY}', yy)
    .replaceAll('{MM}', mm)
    .replaceAll('{RUNNING}', ctx.running ?? '0001')
}

// show the pills row based on current template
export function PreviewTokenRow({ tpl }: { tpl: string }) {
  const items = useMemo(() => parseTemplate(tpl), [tpl])
  const labelFor = (token: Token) => TOKENS.find(t => t.token === token)?.label ?? token
  const isRunning = (it: Item) => it.kind === 'token' && it.value === '{RUNNING}'

  if (!items.length) return null

  return (
    <HStack wrap="wrap" gap={2} mt={3}>
      {items.map((it, idx) => {
        if (it.kind === 'sep') {
          return (
            <Tag.Root key={`sep-${idx}`} variant="outline">
              <Tag.Label>{it.value}</Tag.Label>
            </Tag.Root>
          )
        }

        return (
          <Tag.Root key={`tok-${idx}`} variant="subtle" colorPalette="gray">
            <Tooltip.Root openDelay={300} positioning={{ placement: 'bottom' }}>
              <Tooltip.Trigger asChild>
                <Tag.Label cursor="help">
                  {labelFor(it.value)}
                  {isRunning(it) ? ' (Pflichtfeld)' : null}
                </Tag.Label>
              </Tooltip.Trigger>

              {/*  This prevents layout shifting */}
              <Portal>
                <Tooltip.Positioner>
                  <Tooltip.Content>
                    {TOKEN_TOOLTIPS[it.value]}
                    <Tooltip.Arrow />
                  </Tooltip.Content>
                </Tooltip.Positioner>
              </Portal>
            </Tooltip.Root>
          </Tag.Root>
        )
      })}
    </HStack>
  )
}

export function InvoiceNumberTemplateBuilder(props: {
  value: string
  onChange: (value: string) => void
  prefix?: string
  running?: string
}) {
  const { value, onChange } = props

  const [items, setItems] = useState<Item[]>(() => parseTemplate(value))
  // const [sepDraft, setSepDraft] = useState('')

  useEffect(() => {
    setItems(parseTemplate(value))
  }, [value])

  const tpl = useMemo(() => stringify(items), [items])

  useEffect(() => {
    if (tpl !== value) onChange(tpl)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tpl])

  const labelFor = (token: Token) => TOKENS.find(t => t.token === token)?.label ?? token
  const isRunning = (it: Item) => it.kind === 'token' && it.value === '{RUNNING}'

  const addItem = (it: Item) => {
    setItems(prev => {
      const next = [...prev]

      // if user typed separators in draft, commit them before adding token
      // if (sepDraft.trim() !== '') {
      //   next.push({ kind: 'sep', value: sepDraft })
      //   setSepDraft('')
      // }
      next.push(it)
      return next
    })
  }

  const removeAt = (idx: number) => {
    setItems(prev => {
      const next = prev.slice()
      next.splice(idx, 1)

      // merge adjacent separators
      const merged: Item[] = []
      for (const it of next) {
        const last = merged[merged.length - 1]
        if (it.kind === 'sep' && last?.kind === 'sep') last.value += it.value
        else merged.push({ ...it })
      }
      return merged.filter(i => i.value !== '')
    })
  }

  return (
    <VStack align="stretch" gap={3}>
      {/* Big input-like editor */}
      <Box>
        <Card.Root variant="outline">
          <Card.Body display="flex" alignItems="center" flexWrap="wrap" gap={2}>
            {items.length === 0 && <Card.Description>Serie konfigurieren…</Card.Description>}

            {items.map((it, idx) => {
              if (it.kind === 'sep') {
                return (
                  <Tag.Root key={`sep-${idx}`} variant="outline">
                    <Tag.Label>{it.value}</Tag.Label>
                    <Tag.CloseTrigger
                      onClick={() => removeAt(idx)}
                      aria-label="remove separator"
                    />
                  </Tag.Root>
                )
              }

              return (
                <Tag.Root key={`tok-${idx}`} variant="subtle" colorPalette="gray">
                  <Tag.Label>
                    {labelFor(it.value)}
                    {isRunning(it) ? ' (Pflichtfeld)' : null}
                  </Tag.Label>
                  <Tag.CloseTrigger onClick={() => removeAt(idx)} aria-label="remove" />
                </Tag.Root>
              )
            })}
          </Card.Body>
        </Card.Root>

        <Text>
          Pflichtfelder: Zähler, JJJJ / JJ (nur bei jährlicher Zurücksetzung)
        </Text>
      </Box>

      {/* Token buttons row like mockup */}
      <Box>
        <Text>Serie konfigurieren</Text>

        <HStack wrap="wrap" gap={2}>
          {DEFAULT_BUTTONS.map(b => {
            const tooltip = b.add.kind === 'token' ? TOKEN_TOOLTIPS[b.add.value] : 'Trennzeichen'

            return (
              <Tooltip.Root
                key={b.label}
                openDelay={300}
                positioning={{ placement: 'top' }} // or "bottom"
              >
                <Tooltip.Trigger asChild>
                  <Button variant="subtle" onClick={() => addItem(b.add)}>
                    {b.label}
                  </Button>
                </Tooltip.Trigger>

                <Portal>
                  <Tooltip.Positioner>
                    <Tooltip.Content>
                      {tooltip}
                      <Tooltip.Arrow />
                    </Tooltip.Content>
                  </Tooltip.Positioner>
                </Portal>
              </Tooltip.Root>
            )
          })}
        </HStack>
      </Box>
    </VStack>
  )
}
